import React, { useState, useEffect } from 'react';
import NavigationBar from './NavigationBar';
import './CantileverStressCalculator.css';

interface Material {
  id: number;
  name: string;
  value: number;
}

interface CalculationResult {
  force: number;
  calculatedSigma: number;
  elasticRange: number | null;
  deflection: number;
}

const CantileverStressCalculator: React.FC = () => {
  const [materials, setMaterials] = useState<Material[]>([
    { id: 1, name: 'C1720', value: 127000 },
    { id: 2, name: '7合金', value: 127000 },
    { id: 3, name: '8·11合金', value: 132000 },
    { id: 4, name: 'YCUT-FX', value: 125000 },
    { id: 5, name: 'C2680(H65)', value: 105000 },
    { id: 6, name: 'C2800(H62)', value: 105000 },
    { id: 7, name: 'C2600(H70)', value: 105000 },
    { id: 8, name: 'C5191', value: 115000 },
    { id: 9, name: 'C5210', value: 110000 },
    { id: 10, name: 'C7035', value: 120000 },
    { id: 11, name: 'C7025', value: 131000 },
    { id: 12, name: 'C1020', value: 118000 },
    { id: 13, name: 'C1100(T2)', value: 118000 },
    { id: 14, name: 'C18400', value: 137000 },
    { id: 15, name: 'C18140', value: 137000 },
    { id: 16, name: 'C18150', value: 137000 },
    { id: 17, name: 'C7521', value: 125000 },
    { id: 18, name: 'C7701', value: 125000 },
    { id: 19, name: 'SUS301', value: 196000 },
    { id: 20, name: 'SUS304', value: 193000 },
  ]);

  const [selectedMaterial, setSelectedMaterial] = useState<string>('');
  const [elasticModulus, setElasticModulus] = useState<string>('');
  const [displacement, setDisplacement] = useState<string>('');
  const [thickness, setThickness] = useState<string>('');
  const [length, setLength] = useState<string>('');
  const [width, setWidth] = useState<string>('');
  const [yieldStrength, setYieldStrength] = useState<string>('');
  const [result, setResult] = useState<CalculationResult | null>(null);

  const handleMaterialChange = (materialId: string) => {
    setSelectedMaterial(materialId);

    if (materialId) {
      const material = materials.find(m => m.id.toString() === materialId);
      if (material) {
        setElasticModulus(material.value.toString());
      }
    } else {
      setElasticModulus('');
    }
  };

  const calculate = () => {
    const E = parseFloat(elasticModulus);
    const d = parseFloat(displacement);
    const t = parseFloat(thickness);
    const L = parseFloat(length);
    const w = parseFloat(width);
    const sigma = parseFloat(yieldStrength);

    if (!E || !d || !t || !L || !w) {
      alert('请填写所有必要的参数！');
      return;
    }

    // 计算正向力 P = d * E * w * t³ / (4 * L³)
    const P = (d * E * w * Math.pow(t, 3)) / (4 * Math.pow(L, 3));

    // 计算屈服强度 σ = 3 * d * E * t / (2 * L²)
    const calculatedSigma = (3 * d * E * t) / (2 * Math.pow(L, 2));

    // 计算弹性范围 D = 2 * L² * σ / (3 * E * t)
    let elasticRange: number | null = null;
    if (sigma) {
      elasticRange = (2 * Math.pow(L, 2) * sigma) / (3 * E * t);
    }

    // 计算位移挠度 nd = 4 * P * L³ / (E * w * t³)
    const nd = (4 * P * Math.pow(L, 3)) / (E * w * Math.pow(t, 3));

    setResult({
      force: P,
      calculatedSigma,
      elasticRange,
      deflection: nd,
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      calculate();
    }
  };

  useEffect(() => {
    document.addEventListener('keypress', handleKeyPress as any);
    return () => {
      document.removeEventListener('keypress', handleKeyPress as any);
    };
  }, []);

  return (
    <div className="cantilever-stress-calculator">
      <NavigationBar title="悬臂梁应力计算器" />

      <div className="calculator-section">
        <div className="form-group">
          <label>材料牌号：</label>
          <select
            value={selectedMaterial}
            onChange={e => handleMaterialChange(e.target.value)}
          >
            <option value="">请选择材料</option>
            {materials.map(material => (
              <option key={material.id} value={material.id}>
                {material.name} (E={material.value} MPa)
              </option>
            ))}
          </select>
        </div>

        <div className="formula-section">
          <h4>计算公式</h4>
          <div className="formula">正向力 P = d × E × w × t³ / (4 × L³)</div>
          <div className="formula">屈服强度 σ = 3 × d × E × t / (2 × L²)</div>
          <div className="formula">弹性范围 D = 2 × L² × σ / (3 × E × t)</div>
          <div className="formula">位移挠度 nd = 4 × P × L³ / (E × w × t³)</div>
          <div className="formula-image">
            <img
              src="https://cfbed.586186.xyz/file/1758193634259_e6dVX28OH.png"
              alt="公式示意图"
            />
          </div>
        </div>

        <div className="input-row">
          <div className="form-group">
            <label>弹性模量 E (MPa)：</label>
            <input
              type="number"
              value={elasticModulus}
              onChange={e => setElasticModulus(e.target.value)}
              placeholder="选择材料后自动填充"
              readOnly
            />
          </div>

          <div className="form-group">
            <label>位移 d (mm)：</label>
            <input
              type="number"
              value={displacement}
              onChange={e => setDisplacement(e.target.value)}
              placeholder="输入位移"
              step="0.01"
            />
          </div>
        </div>

        <div className="input-row">
          <div className="form-group">
            <label>厚度 t (mm)：</label>
            <input
              type="number"
              value={thickness}
              onChange={e => setThickness(e.target.value)}
              placeholder="输入厚度"
              step="0.01"
            />
          </div>

          <div className="form-group">
            <label>长度 L (mm)：</label>
            <input
              type="number"
              value={length}
              onChange={e => setLength(e.target.value)}
              placeholder="输入长度"
              step="0.01"
            />
          </div>
        </div>

        <div className="input-row">
          <div className="form-group">
            <label>宽度 w (mm)：</label>
            <input
              type="number"
              value={width}
              onChange={e => setWidth(e.target.value)}
              placeholder="输入宽度"
              step="0.01"
            />
          </div>

          <div className="form-group">
            <label>屈服强度 σ (MPa)：</label>
            <input
              type="number"
              value={yieldStrength}
              onChange={e => setYieldStrength(e.target.value)}
              placeholder="输入屈服强度"
              step="0.1"
            />
          </div>
        </div>

        <button className="calculate-btn" onClick={calculate}>
          计算
        </button>

        {result && (
          <div className="results">
            <h3>计算结果</h3>
            <div className="result-item">
              <span className="result-label">正向力 P：</span>
              <span className="result-value">{result.force.toFixed(4)} N</span>
            </div>
            <div className="result-item">
              <span className="result-label">屈服强度 σ：</span>
              <span className="result-value">
                {result.calculatedSigma.toFixed(4)} MPa
              </span>
            </div>
            <div className="result-item">
              <span className="result-label">弹性范围 D：</span>
              <span className="result-value">
                {result.elasticRange
                  ? `${result.elasticRange.toFixed(4)} mm`
                  : '-'}
              </span>
            </div>
            <div className="result-item">
              <span className="result-label">位移挠度 nd：</span>
              <span className="result-value">
                {result.deflection.toFixed(4)} mm
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CantileverStressCalculator;
